import { canadianProvicesAbbreviations } from "../countries/canada/provinces";
import { usStatesAbbreviations } from "../countries/us/states";
import Holidays from "./Holidays.json";

/**
 * Country names to filter holidays
 */
export type CountryName = "Canada" | "UnitedStates";

/**
 * Useful to know which states celebrate a holiday and which not
 */
interface StateRules {
  allow: string[];
  except: string[];
}

/**
 * A holiday
 * @property description - Description of the holiday. e.g. "New Year"
 * @property dates - A list with all the dates for that holiday through multiple years e.g. [[2021, 1, 1], [2022, 1, 1,]]
 * @property stateRules - Rules of which states celebrate the holiday
 */
export interface Holiday {
  description: string;
  dates: number[][];
  stateRules: StateRules;
}

/**
 * Index for the year in a date list
 */
const HOLIDAY_YEAR = 0;
/**
 * Index for the month in a date list
 */
const HOLIDAY_MONTH = 1;
/**
 * Index for the day number in a date list
 */
const HOLIDAY_DAY = 2;

function getHolidays(): Holiday[] {
  return Holidays;
}

/**
 * Represents a simple date
 *
 * @property year Year of the date
 * @property month Month of the year (1-12)
 * @property day Day of the month (1-31)
 */
interface Date {
  year: number;
  month: number;
  day: number;
}

/**
 * Parses the date from the 'Holidays.json' file
 */
function toDate(date: number[]): Date {
  const year = date[HOLIDAY_YEAR];
  const month = date[HOLIDAY_MONTH];
  const day = date[HOLIDAY_DAY];
  if (year === undefined || month === undefined || day === undefined) {
    throw new Error(`Malformed holiday date entry: ${JSON.stringify(date)}`);
  }
  return { year, month, day };
}

/**
 * Compares a date from the params with a date from a holiday date line
 */
function compareDates(
  year: number,
  month: number,
  day: number,
  date: number[],
): boolean {
  if (!Number.isInteger(year)) {
    throw TypeError("Year should be an integer");
  }

  if (!Number.isInteger(month)) {
    throw TypeError("Month should be an integer");
  }

  if (!Number.isInteger(day)) {
    throw TypeError("Day should be an integer");
  }

  const holidayDate = toDate(date);

  return (
    year === holidayDate.year &&
    month === holidayDate.month &&
    day === holidayDate.day
  );
}

/**
 * Searches for a date from the params in the list of dates of a holiday
 */
function findDateInHolidayDates(
  year: number,
  month: number,
  day: number,
  holiday: Holiday,
) {
  return holiday.dates.find((date) => compareDates(year, month, day, date));
}

/**
 * Canada and the US observe New Year's Day and Christmas Day on different dates
 * when Jan 1 or Dec 25 lands on a Saturday: US federal rules move the observance
 * back to the Friday, Canada forward to the Monday. One row can hold only one of
 * those rules, so the calendar carries a row per country for those two holidays.
 *
 * On every date where the two countries agree, both rows match, and a caller
 * asking "what falls on this day" wants the one holiday a customer would name —
 * not the same name twice. Merging unions the states the matching rows allow, so
 * `getCountriesOfHoliday` on the result still names both countries.
 *
 * `except` is resolved before the union: a province carved out of one row must not
 * be reinstated by the other.
 *
 * The merged row describes the date that was asked about. Its `dates` is the union
 * of the rows that matched, so it is not a calendar to re-query — use it for the
 * name and the countries.
 */
function mergeHolidaysSharingADescription(holidays: Holiday[]): Holiday[] {
  const merged = new Map<string, Holiday>();

  for (const holiday of holidays) {
    const observedIn = holiday.stateRules.allow.filter(
      (state) => !holiday.stateRules.except.includes(state),
    );

    const alreadyMerged = merged.get(holiday.description);
    if (alreadyMerged === undefined) {
      merged.set(holiday.description, {
        ...holiday,
        stateRules: { allow: observedIn, except: [] },
      });
      continue;
    }

    merged.set(holiday.description, {
      ...alreadyMerged,
      dates: [...alreadyMerged.dates, ...holiday.dates],
      stateRules: {
        allow: [...new Set([...alreadyMerged.stateRules.allow, ...observedIn])],
        except: [],
      },
    });
  }

  return [...merged.values()];
}

/**
 * @param year Year
 * @param month Month of the year - between 1 and 12
 * @param day Day of the month - between 1 and 31
 * @return A list of holidays matching the date from the params
 */
export function getHolidaysByDate(
  year: number,
  month: number,
  day: number,
): Holiday[] {
  return mergeHolidaysSharingADescription(
    getHolidays().filter(
      (holiday) =>
        findDateInHolidayDates(year, month, day, holiday) !== undefined,
    ),
  );
}

/**
 * @return A list of holidays celebrated in the specified state
 *
 * `except` carves a province back out of `allow`: Civic Day lists every
 * Canadian province including Quebec, then excepts Quebec, which observes
 * Quebec National Day instead. Honouring only `allow` matched Quebec to a
 * holiday it doesn't keep. The backend has always applied both lists
 * (InMemoryHolidayManager).
 */
export function getHolidaysForState(state: string): Holiday[] {
  if (
    !usStatesAbbreviations.includes(state) &&
    !canadianProvicesAbbreviations.includes(state)
  ) {
    return [];
  }

  const holidays = getHolidays();

  return holidays.filter(
    (holiday) =>
      holiday.stateRules.allow.includes(state) &&
      !holiday.stateRules.except.includes(state),
  );
}

/**
 * @return A list of holidays celebrated in the specified country
 */
export function getHolidaysForCountry(country: CountryName): Holiday[] {
  if (country !== "Canada" && country !== "UnitedStates") {
    return [];
  }

  const holidays = getHolidays();

  if (country === "UnitedStates") {
    return holidays.filter((holiday) =>
      getCountriesOfHoliday(holiday).includes(HOLIDAY_US_DESCRIPTION),
    );
  }

  return holidays.filter((holiday) =>
    getCountriesOfHoliday(holiday).includes(HOLIDAY_CANADA_DESCRIPTION),
  );
}

export const HOLIDAY_US_DESCRIPTION = "the United States";
export const HOLIDAY_CANADA_DESCRIPTION = "Canada";

/**
 * @return A list of country names that celebrate the specified holiday. e.g. ['Canada', 'the United States']
 */
export function getCountriesOfHoliday(holiday: Holiday): string[] {
  const countries = [];

  /**
   * Count of canadian provinces allowed in the current holiday
   */
  const canada = holiday.stateRules.allow.filter((allowed) =>
    canadianProvicesAbbreviations.includes(allowed),
  ).length;

  /**
   * Count of us states allowed in the current holiday
   */
  const us = holiday.stateRules.allow.filter((allowed) =>
    usStatesAbbreviations.includes(allowed),
  ).length;

  if (canada > 0) {
    countries.push(HOLIDAY_CANADA_DESCRIPTION);
  }

  if (us > 0) {
    countries.push(HOLIDAY_US_DESCRIPTION);
  }

  return countries;
}

export function isHolidayForState(
  year: number,
  month: number,
  day: number,
  state: string,
): boolean {
  return (
    getHolidaysForState(state).filter(
      (holiday) =>
        findDateInHolidayDates(year, month, day, holiday) !== undefined,
    ).length > 0
  );
}

export function isHoliday(year: number, month: number, day: number): boolean {
  return getHolidaysByDate(year, month, day).length > 0;
}

export function isHolidayForCountry(
  year: number,
  month: number,
  day: number,
  country: CountryName,
): boolean {
  return (
    getHolidaysForCountry(country).filter(
      (holiday) =>
        findDateInHolidayDates(year, month, day, holiday) !== undefined,
    ).length > 0
  );
}
